'use client';

import { usePathname } from 'next/navigation';
import { useEffect } from 'react';
import { getUTMsFromCookies } from '@/hooks/useUTMPersistence';

/**
 * Watches for DOM mutations (e.g. dynamically rendered service cards)
 * and re-applies persisted UTM params to any new internal links.
 */
export default function UTMObserver() {
  const pathname = usePathname();

  useEffect(() => {
    if (typeof window === 'undefined') return;

    let observer: MutationObserver | null = null;
    let debounceTimer: ReturnType<typeof setTimeout>;

    const applyUTMsToLinks = () => {
      const utms = getUTMsFromCookies();
      if (!Object.keys(utms).length) return;

      document.querySelectorAll<HTMLAnchorElement>('a[href]').forEach((anchor) => {
        try {
          const href = anchor.getAttribute('href');
          if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) return;

          const url = new URL(href, window.location.origin);
          if (url.origin !== window.location.origin) return;

          let changed = false;
          Object.entries(utms).forEach(([key, val]) => {
            if (!url.searchParams.has(key)) {
              url.searchParams.set(key, val);
              changed = true;
            }
          });

          if (changed) {
            anchor.setAttribute('href', url.pathname + '?' + url.searchParams.toString() + url.hash);
          }
        } catch {
          // skip malformed
        }
      });
    };

    // Delay all DOM modifications until after React hydration completes
    const startDelay = setTimeout(() => {
      applyUTMsToLinks();

      // Debounced observer for lazily-rendered content
      observer = new MutationObserver(() => {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(applyUTMsToLinks, 500);
      });

      observer.observe(document.body, { childList: true, subtree: true });
    }, 3000);

    return () => {
      clearTimeout(startDelay);
      clearTimeout(debounceTimer);
      observer?.disconnect();
    };
  }, [pathname]);

  return null;
}
